/**
 * Job Runs — admin-only read-only surface at /admin/runs (W4b-β2).
 *
 * A BuildTracker.tsx port to public.job_runs: the run log for the acquisition workers
 * (resolve-matches / fetch-amazon-facts / fetch-ebay-prices) AND the ops crons
 * (check-role-drift / cron-task-overdue / weekly-digest / spapi-smoke) — a GENERIC job-run
 * log, hence the generic "Job Runs" label, not "Acquisition Runs". Each worker writes its
 * own job_runs row per invocation (a start row ok=false/phase='started', finalized at the
 * end) — this view renders those EXISTING rows; NO worker amendment.
 *
 * Datasource — PostgREST-direct on public.job_runs (admin-only SELECT RLS), { count:'exact' }
 * + .range(...), ordered ran_at DESC (the timeline). READ-ONLY like BuildTracker/AlgopixJobMonitor:
 * NO bulk bar, NO checkbox column, NO triage RPC, NO nav badge (a run is a terminal fact).
 *
 * Status badge: ok=true→success, ok=false & phase='started'→neutral (in-flight or died mid-run),
 * finalized ok=false→danger (TONE §12 / UI_STANDARDS §10). Status filter is All / OK / Failed.
 */

import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
} from 'ag-grid-community';
import { Search, X } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import {
  runTone,
  runLabel,
  runSummary,
  runDetailEntries,
  jobRunSearchOrFilter,
  type RunTone,
} from '../lib/jobRuns';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ───────────────────────────────────────────────────────────────

type StatusFilter = 'ok' | 'failed' | null;
type TimeFilter = '7d' | '30d' | 'all';

interface JobRunRow {
  id: string;
  job_name: string;
  ran_at: string;
  ok: boolean;
  detail: Record<string, unknown> | null;
}

function rowPhase(row: JobRunRow): string | null {
  return typeof row.detail?.phase === 'string' ? (row.detail.phase as string) : null;
}

function rowError(row: JobRunRow): string | null {
  return typeof row.detail?.error === 'string' ? (row.detail.error as string) : null;
}

// ── Filter constants ──────────────────────────────────────────────────────────

const STATUS_FILTERS: ReadonlyArray<{ id: StatusFilter; label: string }> = [
  { id: null, label: 'All' },
  { id: 'ok', label: 'OK' },
  { id: 'failed', label: 'Failed' },
] as const;

// Default 'all' (not '7d'): job_runs is a modest append-only log, and an 'all' default keeps
// hasFilters=false on a fresh empty table so the first-time empty state shows correctly.
const TIME_FILTERS: ReadonlyArray<{ id: TimeFilter; label: string }> = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'all', label: 'All' },
] as const;

function timeFilterCutoff(t: TimeFilter): string | null {
  if (t === 'all') return null;
  const ms = t === '7d' ? 604_800_000 : 2_592_000_000;
  return new Date(Date.now() - ms).toISOString();
}

// ── Status badge ────────────────────────────────────────────────────────────────

const TONE_CLASS: Record<RunTone, string> = {
  success: 'bg-success/15 text-success',
  danger: 'bg-danger/15 text-danger',
  neutral: 'bg-surface-3 text-ink-muted',
};

function RunBadge({ ok, phase }: { ok: boolean; phase: string | null }): JSX.Element {
  return (
    <span
      className={clsx(
        'px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap',
        TONE_CLASS[runTone(ok, phase)]
      )}
    >
      {runLabel(ok, phase)}
    </span>
  );
}

// ── Relative time (inline-replicated per BuildTracker/Algopix precedent) ─────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Filter pill component (inline-replicated) ───────────────────────────────────

interface FilterGroupProps<T extends string | null> {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}

function FilterGroup<T extends string | null>({
  options,
  value,
  onChange,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
      {options.map((f) => (
        <button
          key={f.id ?? 'all'}
          onClick={() => onChange(f.id)}
          aria-pressed={value === f.id}
          className={clsx(
            'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
            value === f.id ? 'bg-surface-4 text-ink-bright' : 'text-ink-muted hover:text-ink-secondary'
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Detail panel (slide-in; read-only) ──────────────────────────────────────────

function JobRunDetailPanel({ row, onClose }: { row: JobRunRow; onClose: () => void }): JSX.Element {
  const [showRaw, setShowRaw] = useState<boolean>(false);

  useEffect(() => {
    setShowRaw(false);
  }, [row.id]);

  const phase = rowPhase(row);
  const error = rowError(row);
  const entries = runDetailEntries(row.detail);

  return (
    <div className="w-96 flex-shrink-0 bg-surface-1 border-l border-border h-full overflow-y-auto pr-2.5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-ink-bright truncate pr-2">{row.job_name}</h3>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <RunBadge ok={row.ok} phase={phase} />
          {phase && (
            <span className="text-2xs font-mono text-ink-muted px-1.5 py-0.5 rounded bg-surface-3">
              {phase}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Job</p>
            <p className="text-xs text-ink-primary break-words">{row.job_name}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Ran</p>
            <p className="text-2xs font-mono text-ink-muted">{relativeTime(row.ran_at)}</p>
          </div>
        </div>

        {error && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Error</p>
            <p className="text-xs text-danger whitespace-pre-wrap break-words">{error}</p>
          </div>
        )}

        {entries.length > 0 && (
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Counts</p>
            <div className="space-y-1">
              {entries.map((e) => (
                <div key={e.key} className="flex items-center justify-between text-xs">
                  <span className="text-ink-muted font-mono">{e.key}</span>
                  <span className="text-ink-primary font-mono">{e.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Raw detail jsonb (collapsed by default) */}
        <div className="border-t border-border-subtle pt-3">
          <button
            onClick={() => setShowRaw((s) => !s)}
            className="text-2xs text-accent hover:underline"
          >
            {showRaw ? 'Hide raw detail' : 'Show raw detail'}
          </button>
          {showRaw && (
            <pre className="mt-2 text-2xs font-mono text-ink-secondary bg-surface-2 rounded p-2 whitespace-pre-wrap break-all max-h-80 overflow-y-auto">
              {JSON.stringify(row.detail, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function JobRunsViewer(): JSX.Element {
  useDocumentTitle('Job Runs');

  const gridRef = useRef<AgGridReact<JobRunRow> | null>(null);
  const gridApiRef = useRef<GridApi<JobRunRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [detailRow, setDetailRow] = useState<JobRunRow | null>(null);

  const hasFilters = Boolean(statusFilter || timeFilter !== 'all' || searchText.trim());

  const clearFilters = useCallback((): void => {
    setStatusFilter(null);
    setTimeFilter('all');
    setSearchText('');
  }, []);

  const stateRef = useRef<{
    searchText: string;
    statusFilter: StatusFilter;
    timeFilter: TimeFilter;
  }>({ searchText, statusFilter, timeFilter });
  stateRef.current = { searchText, statusFilter, timeFilter };

  // ── Datasource — PostgREST direct on job_runs ──────────────────
  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf, timeFilter: tf } = stateRef.current;

    try {
      let query = supabase
        .from('job_runs')
        .select('id, job_name, ran_at, ok, detail', { count: 'exact' })
        .order('ran_at', { ascending: false });

      if (sf === 'ok') query = query.eq('ok', true);
      if (sf === 'failed') query = query.eq('ok', false);

      const cutoff = timeFilterCutoff(tf);
      if (cutoff) query = query.gte('ran_at', cutoff);

      if (st.trim()) query = query.or(jobRunSearchOrFilter(st));

      // IGetRowsParams.endRow is exclusive; PostgREST .range is inclusive.
      const { data, error, count } = await query.range(startRow, endRow - 1);
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as JobRunRow[];
      const total = count ?? 0;
      setTotalCount(total);
      setLoaded(true);
      successCallback(rows, total);
    } catch (e) {
      reportDatasourceFailure(
        'JobRunsViewer',
        { rpcName: 'job_runs', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<JobRunRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, timeFilter, getRowsFn]);

  // ── Cell renderers ─────────────────────────────────────────────
  const StatusCell = useCallback((p: ICellRendererParams<JobRunRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <RunBadge ok={p.data.ok} phase={rowPhase(p.data)} />;
  }, []);

  const JobCell = useCallback((p: ICellRendererParams<JobRunRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <span className="font-mono text-xs text-ink-primary truncate">{p.data.job_name}</span>;
  }, []);

  const SummaryCell = useCallback((p: ICellRendererParams<JobRunRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <span className="text-xs text-ink-secondary truncate">{runSummary(p.data.detail)}</span>;
  }, []);

  const TimeCell = useCallback((p: ICellRendererParams<JobRunRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <span className="font-mono text-xs text-ink-muted">{relativeTime(p.data.ran_at)}</span>;
  }, []);

  const columnDefs = useMemo<ColDef<JobRunRow>[]>(
    () =>
      [
        {
          headerName: 'Status',
          field: 'ok',
          width: 130,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Job',
          field: 'job_name',
          flex: 1,
          minWidth: 220,
          cellRenderer: JobCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
          tooltipField: 'job_name',
        },
        {
          headerName: 'Summary',
          colId: 'summary',
          width: 220,
          sortable: false,
          cellRenderer: SummaryCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Ran',
          field: 'ran_at',
          width: 120,
          cellRenderer: TimeCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
      ] as ColDef<JobRunRow>[],
    [StatusCell, JobCell, SummaryCell, TimeCell]
  );

  const defaultColDef = useMemo<ColDef<JobRunRow>>(
    () => ({
      sortable: false,
      resizable: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerFontSize: '10px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<JobRunRow>): void => {
    if (!event.data) return;
    const domEvent = event.event as Event | undefined;
    const target = domEvent?.target as HTMLElement | null;
    if (target?.closest('button') || target?.closest('a')) return;
    setDetailRow(event.data);
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Job Runs</h1>
            <span className="text-xs text-ink-muted font-mono">
              {totalCount === 1 ? '1 run' : `${totalCount.toLocaleString()} runs`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <FilterGroup options={STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
          <FilterGroup options={TIME_FILTERS} value={timeFilter} onChange={setTimeFilter} />
          <div className="relative ml-auto">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search job name..."
              aria-label="Search job runs"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
            />
          </div>
        </div>
      </div>

      {/* Grid + optional detail panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2 relative" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={50}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            suppressNoRowsOverlay={true}
          />

          {loaded && totalCount === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              {hasFilters ? (
                <>
                  <p className="text-sm text-ink-secondary">No runs match your current filters</p>
                  <button onClick={clearFilters} className="arbi-btn-ghost text-xs mt-2">
                    Clear filters
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-ink-secondary">No job runs recorded yet</p>
                  <p className="text-xs text-ink-muted mt-1 max-w-xs">
                    Runs appear here when a worker or cron job records an invocation.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {detailRow && <JobRunDetailPanel row={detailRow} onClose={() => setDetailRow(null)} />}
      </div>
    </div>
  );
}
